import { useEffect, useState, useRef } from "react";
import QuickList from "../../common/list-component/quick-list";
import ModalView from "../../common/modal";
import TextInput from "../../common/text-input";
import Button from "../../common/button/button";
import DatePickerComponent from "../../common/date-picker";
import APIService from "../../Service/APIService";
import Swal from "sweetalert2";
import moment from "moment";
import FailureIcon from "../../icons/failure-icon";
import Text from "../../common/typography/text";
import {useAuth} from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import { debounce } from "lodash";
import FileUpload from "../UploadFileNewComponent/UploadFile.js";

const ProductListModule = ({
  items,
  listTitle,
  primaryTitle,
  secondaryTitle,
  price,
  showViewProduct,
  handleShowViewProduct,
  invoiceTotal,
  invoiceData,
}) => {
  const { user } = useAuth();
  const userData = user;
  const [productItem, setProductItem] = useState();
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // form values
  const [productCode, setProductCode] = useState();
  const [tissueID, setTissueID] = useState();
  const [productPrice, setProductPrice] = useState();
  const isPrepaidInvoice = Boolean(invoiceData?.PrepaidOrder);

  const showPrepaidEditBlockedMessage = () => {
    Swal.fire({
      icon: "error",
      title: "Edits Not Allowed",
      text: "Invoice products cannot be edited for prepaid invoices.",
    });
  };

  const getApiErrorMessage = (error, fallbackMessage) =>
    error?.response?.data?.message ||
    error?.response?.data?.data ||
    fallbackMessage;

  const handleReturnModal = () => {
    if (isPrepaidInvoice) {
      showPrepaidEditBlockedMessage();
      return;
    }
    setShowReturnModal(true);
    handleShowViewProduct();
  };

  const handleCompleteModal = () => {
    if (isPrepaidInvoice) {
      showPrepaidEditBlockedMessage();
      return;
    }
    resetCompleteReturnForm();
    setShowCompleteModal(true);
    handleShowViewProduct();
  };

  useEffect(() => {
    if (productItem) {
      const formattedPrice = Number(productItem?.[price]?.replace(/[$,]/g, ""));
      console.log(formattedPrice);
      setProductCode(productItem?.[secondaryTitle]);
      setTissueID(productItem?.[primaryTitle]);
      setProductPrice(formattedPrice);
    }
  }, [productItem]);

  const [returnNote, setReturnNote] = useState("");
  const [returnedOn, setReturnedOn] = useState(null);
  const [returnInvoiceFiles, setReturnInvoiceFiles] = useState([]);
  const isSentToCollection = Boolean(productItem?.IsSentToCollection);
  const sentToCollectionReason =
    "This product has been sent to collections. Tissue ID, cost, and return actions are disabled.";

  const resetCompleteReturnForm = () => {
    setReturnNote("");
    setReturnedOn(null);
    setReturnInvoiceFiles([]);
  };

  const completeReturn = async () => {
    if (isPrepaidInvoice) {
      showPrepaidEditBlockedMessage();
      return;
    }
    console.log("Completing return for product:", productItem);
    // return;
    setShowCompleteModal(false);
    const apiServe = new APIService();
    const postObj = {
      InvoiceProductID: productItem.ID,
      ReturnNote: returnNote,
      ReturnedOn: returnedOn,
      ProductPrice: productPrice,
      InvoiceTotal: invoiceTotal,
      InvoiceID: productItem.InvoiceID,
    };
    try {
      const formData = new FormData();
      formData.append("data", JSON.stringify(postObj));
      if (returnInvoiceFiles[0]) {
        formData.append("file", returnInvoiceFiles[0]);
      }

      const response = await apiServe.addFileDataToBackend(
        formData,
        "completeareturn",
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      console.log("Return completed:", response);
      if (response?.status === 200 || response?.status === 201) {
        Swal.fire(
          "Return Completed!",
          `The return has been completed. The new invoice total will be ${
            Number(invoiceTotal) - Number(productPrice)
          }.`,
          "success"
        ).then(() => {
          window.location.reload();
        });
        return;
      }

      Swal.fire({
        title: "Error!",
        text:
          response?.data?.message ||
          response?.data?.error ||
          "Error while completing return",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: getApiErrorMessage(error, "Error while completing return"),
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      resetCompleteReturnForm();
    }
  };

  const startReturn = async () => {
    if (isPrepaidInvoice) {
      showPrepaidEditBlockedMessage();
      return;
    }
    console.log("Starting return for product:", productItem);
    // return;
    setShowReturnModal(false);
    const apiServe = new APIService();
    const postObj = {
      InvoiceProductID: productItem.ID,
      ReturnNote: null,
    };
    try {
      const response = await apiServe.updateDataToBackend(
        postObj,
        "startareturn"
      );
      console.log("Return started:", response);
      if (response?.status === 200 || response?.status === 201) {
        Swal.fire("Return!", "The return has been initiated.", "success").then(
          () => {
            window.location.reload();
          }
        );
        return;
      }

      Swal.fire({
        title: "Error!",
        text:
          response?.data?.message ||
          response?.data?.error ||
          "Error while starting return",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: getApiErrorMessage(error, "Error while starting return"),
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const cancelReturn = async () => {
    if (isPrepaidInvoice) {
      showPrepaidEditBlockedMessage();
      return;
    }
    console.log("Cancelling return for product:", productItem);
    // return;
    setShowReturnModal(false);
    const apiServe = new APIService();
    const postObj = {
      InvoiceProductID: productItem.ID,
      ReturnNote: null,
    };
    try {
      const response = await apiServe.updateDataToBackend(
        postObj,
        "cancelareturn"
      );
      console.log("Return cancelled:", response);
      if (response?.status === 200 || response?.status === 201) {
        Swal.fire("Return!", "The return has been cancelled.", "success").then(
          () => {
            window.location.reload();
          }
        );
        return;
      }

      Swal.fire({
        title: "Error!",
        text:
          response?.data?.message ||
          response?.data?.error ||
          "Error while cancelling return",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: getApiErrorMessage(error, "Error while cancelling return"),
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  }

  const handleDateChange = async (date) => {
    setReturnedOn(date);
  };

  const [showLoader, setShowLoader] = useState(false);

  const editInvoiceProduct = async () => {
    if (isPrepaidInvoice) {
      showPrepaidEditBlockedMessage();
      return;
    }
    console.log("Editing invoice product:", productItem);
    setShowLoader(true);
    const valid = await validateBeforeAction();
    if (!valid) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tissue IDs",
        text: "Please fix duplicate Tissue IDs before submitting.",
      });
      setShowLoader(false);
      return;
    }
    // return;
    const apiServe = new APIService();
    const postObj = {
      ID: productItem.ID,
      TissueID: tissueID,
      Price: productPrice,
      InvoiceID: productItem.InvoiceID,
      ProductCode: productCode,
    };
    try {
      const response = await apiServe.updateDataToBackend(
        postObj,
        "updateinvoiceproducts?type=InvoiceHomeOrderProductsEdit"
      );
      console.log("Response from backend: ", response);
      if (response?.status === 200 || response?.status === 201) {
        Swal.fire(
          "Product Updated!",
          "The product has been updated successfully.",
          "success"
        ).then(() => {
          window.location.reload();
        });
        return;
      }

      Swal.fire({
        title: "Error!",
        text:
          response?.data?.message ||
          response?.data?.error ||
          "Error while updating product",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: getApiErrorMessage(error, "Error while updating product"),
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const [tissueErrors, setTissueErrors] = useState({});
  const pendingChecksRef = useRef([]);

  const apiServ = new APIService();
  // Debounced checker (runs 500ms after typing stops)
  const checkTissueId = debounce(async (value, recordKey, setErrors) => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [recordKey]: null }));
      return;
    }
    const checkPromise = (async () => {
      try {
        const res = await apiServ.getDataFromBackend(
          `CheckForDuplicateTissueID?TissueID=${value}`
        );
        if (res.data.exists) {
          setErrors((prev) => ({
            ...prev,
            [recordKey]: (
              <>
                Duplicate Tissue ID found in Invoice Number{" "}
                <a
                  href={`/InvoiceHome/${res.data?.invoice?.ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {res.data?.invoice?.InvoiceNumber}
                </a>
              </>
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, [recordKey]: null }));
        }
      } catch {
        setErrors((prev) => ({
          ...prev,
          [recordKey]: "Error checking Tissue ID",
        }));
      }
    })();

    pendingChecksRef.current.push(checkPromise);
    await checkPromise;
    pendingChecksRef.current = pendingChecksRef.current.filter(
      (p) => p !== checkPromise
    );
  }, 500);

  const waitForAllChecks = async () => {
    await Promise.all(pendingChecksRef.current);
  };

  const validateBeforeAction = async () => {
    await waitForAllChecks();

    // Check combined errors
    return !Object.values(tissueErrors).some((err) => err !== null);
  };

  const handleInputChange = (e, key, column) => {
    const value = e.target.value;
    if (column === "TissueID") {
      setTissueID(value);
      // Debounced server check
      checkTissueId(value, key, setTissueErrors);
    }
  }

  return (
    <>
      <QuickList
        listTitle="Products"
        items={items}
        primaryTitle="TissueID"
        secondaryTitle="ProductCode"
        tertiaryTitle="ProductName"
        price="Price"
        showViewProduct={showViewProduct}
        handleShowViewProduct={handleShowViewProduct}
        setProductItem={setProductItem}
        page="Invoices"
      />
      {showViewProduct && userData?.Type === "Admin" && hasPermission(userData, "product.edit") && (
        <ModalView
          isOpen={showViewProduct}
          onClose={handleShowViewProduct}
          modalTitle="View Product"
          modalSubTitle={
            <>
              {productItem?.IsSentToCollection && (
                <>
                  {sentToCollectionReason}
                  <br />
                </>
              )}
              {productItem?.IsReturned && (
                <>
                  Return was completed by {productItem?.ReturnedBy} on{" "}
                  {moment(productItem?.ReturnedOn).format("MM-DD-YYYY")}
                  <br />
                  Return Reason: {productItem?.ReturnNote}
                </>
              )}
            </>
          }
          textInputComponent={
            <div
              style={{ display: "flex", flexDirection: "column", gap: "32px" }}
            >
              {isSentToCollection && (
                <div className="modal-warning-container">
                  <FailureIcon
                    className="warning-icon-container"
                    width={16}
                    height={16}
                  />
                  <Text>{sentToCollectionReason}</Text>
                </div>
              )}
              {invoiceData?.IsCommissionApplied && (
                <div className="modal-warning-container">
                  <FailureIcon
                    className="warning-icon-container"
                    width={16}
                    height={16}
                  />
                  <Text>
                    TissueID and cost cannot be changed after commission has
                    been applied to the invoice.
                  </Text>
                </div>
              )}
              {isPrepaidInvoice && (
                <div className="modal-warning-container">
                  <FailureIcon
                    className="warning-icon-container"
                    width={16}
                    height={16}
                  />
                  <Text>
                    Invoice products cannot be edited for prepaid invoices.
                  </Text>
                </div>
              )}
              <div className="field-group-wrapper">
                <div className="field-group">
                  <TextInput
                    titleText="Product Code"
                    placeholder="Product Code"
                    isRequired
                    value={productCode}
                    isDisabled={true}
                  />
                  <TextInput
                    titleText="Tissue ID"
                    placeholder="Tissue ID"
                    isRequired
                    value={tissueID}
                    setValue={(val) => {
                      // const updated = [...serializedProducts];
                      // updated[index] = { ...updated[index], TissueID: val };
                      // setSerializedProducts(updated);
                      handleInputChange(
                        { target: { value: val } },
                        0,
                        "TissueID"
                      );
                    }}
                    errorText={
                      tissueErrors[0]
                        ? tissueErrors[0]
                        : null
                    }
                    isDisabled={
                      invoiceData?.IsCommissionApplied ||
                      productItem?.IsReturned ||
                      isPrepaidInvoice || 
                      productItem?.IsSentToCollection
                    }
                  />
                  <TextInput
                    titleText="Cost"
                    placeholder="Cost"
                    isRequired
                    value={productPrice}
                    setValue={setProductPrice}
                    isDisabled={
                      invoiceData?.IsCommissionApplied ||
                      productItem?.IsReturned ||
                      isPrepaidInvoice || 
                      productItem?.IsSentToCollection
                    }
                  />
                </div>
              </div>
            </div>
          }
          footerButtonWrapper={
            <>
              {!invoiceData?.IsCommissionApplied && !isPrepaidInvoice && (
                <div className="footer-button-wrapper">
                  {!productItem?.IsReturned && !productItem?.IsSentToCollection && (
                    <Button
                      variant="table"
                      subVariant="success"
                      onClick={editInvoiceProduct}
                      disabled={showLoader}
                    >
                      Save
                    </Button>
                  )}
                  {!productItem?.IsReturned && !productItem?.IsSentToCollection && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      {userData?.Type === "Admin" &&
                        hasPermission(userData, "product.return") &&
                        !productItem.ReturnStarted && (
                          <Button
                            variant="table"
                            subVariant="failure"
                            onClick={handleReturnModal}
                          >
                            Start a Return
                          </Button>
                        )}
                      {userData?.Type === "Admin" &&
                        hasPermission(userData, "product.return") &&
                        !productItem?.ReturnCompleted &&
                        productItem.ReturnStarted && (
                          <>
                            <Button
                              variant="table"
                              subVariant="failure"
                              onClick={handleCompleteModal}
                            >
                              Complete Return
                            </Button>
                            <Button
                              variant="table"
                              subVariant="failure"
                              onClick={cancelReturn}
                            >
                              Cancel Return
                            </Button>
                          </>
                        )}
                    </div>
                  )}
                </div>
              )}
            </>
          }
        />
      )}
      {showReturnModal && userData?.Type === "Admin" && hasPermission(userData, "product.return") && (
        <ModalView
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          modalTitle="Start a Return"
          textInputComponent={`You are starting a return for the Tissue ID ${productItem?.[primaryTitle]}. The return will be pending until it the product is received by the manufacturer and processed.`}
          footerButtonWrapper={
            <div className="footer-button-wrapper">
              <Button variant="ghost" onClick={() => setShowReturnModal(false)}>
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="failure"
                onClick={startReturn}
              >
                Start Return
              </Button>
            </div>
          }
        />
      )}
      {showCompleteModal && userData?.Type === "Admin" && hasPermission(userData, "product.return") && (
        <ModalView
          isOpen={showCompleteModal}
          onClose={() => {
            setShowCompleteModal(false);
            resetCompleteReturnForm();
          }}
          modalTitle="Complete Return"
          textInputComponent={
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                {`You are completing the return for the Tissue ID ${tissueID}. The new invoice total will be ${invoiceTotal - productPrice
                  }.`}
                <div className="field-group-wrapper">
                  <div className="field-group">
                    {/* <DatePickerComponent
                      format="MM-DD-YYYY"
                      titleText="Returned On"
                      isRequired
                      value={returnedOn}
                      setValue={setReturnedOn}
                    /> */}
                    <DatePickerComponent
                      format="MM-DD-YYYY"
                      titleText="Returned On"
                      isRequired
                      value={returnedOn}
                      onChange={handleDateChange}
                    />
                  </div>
                </div>
                <div className="field-group-wrapper">
                  <div className="field-group">
                    <TextInput
                      titleText="Note"
                      placeholder="Enter any notes here"
                      isRequired
                      parentClassName="full-width-button"
                      value={returnNote}
                      setValue={setReturnNote}
                    />
                  </div>
                </div>
                <FileUpload
                  label="Attach New Invoice"
                  uploadedFiles={returnInvoiceFiles}
                  setUploadedFiles={setReturnInvoiceFiles}
                  multiple={false}
                  maxFiles={1}
                />
              </div>
            </>
          }
          footerButtonWrapper={
            <div className="footer-button-wrapper">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCompleteModal(false);
                  resetCompleteReturnForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="failure"
                onClick={completeReturn}
                disabled={!(returnNote && returnedOn)}
              >
                Complete Return
              </Button>
            </div>
          }
        />
      )}
    </>
  );
};

export default ProductListModule;
